import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI } from '../services/api';
import { Skeleton } from '@mui/material';

function MyCourses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, completed

  useEffect(() => {
    fetchEnrolledCourses();
  }, []);

  const fetchEnrolledCourses = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getEnrolledCourses();
      setCourses(response.data.courses || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percent) => {
    if (percent === 100) return '#48bb78';
    if (percent >= 50) return '#667eea';
    if (percent > 0) return '#ed8936';
    return '#a0aec0';
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: { bg: '#c6f6d5', text: '#22543d', icon: '🟢' },
      completed: { bg: '#bee3f8', text: '#2c5282', icon: '✓' },
      dropped: { bg: '#fed7d7', text: '#c53030', icon: '✕' }
    };
    const config = colors[status] || colors.active;
    
    return (
      <span style={{
        padding: '4px 10px',
        background: config.bg,
        color: config.text,
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {config.icon} {status}
      </span>
    );
  };

  const filteredCourses = courses.filter(course => {
    if (filter === 'all') return true;
    if (filter === 'active') return course.enrollment?.status === 'active';
    if (filter === 'completed') return course.enrollment?.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px' }}>
        {/* Header skeleton */}
        <div style={{ marginBottom: '24px' }}>
          <Skeleton variant="text" width="40%" height={32} sx={{ marginBottom: '8px' }} />
          <Skeleton variant="text" width="60%" height={20} />
        </div>

        {/* Filter buttons skeleton */}
        <div style={{
          marginBottom: '20px',
          display: 'flex',
          gap: '12px'
        }}>
          <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rectangular" width={110} height={36} sx={{ borderRadius: '6px' }} />
        </div>

        {/* Courses grid skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
                background: '#ffffff'
              }}
            >
              {/* Thumbnail skeleton */}
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={180}
                sx={{ bgcolor: '#f7fafc' }}
              />
              
              <div style={{ padding: '20px' }}>
                {/* Title and status badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: '6px' }} />
                </div>
                
                {/* Course code */}
                <Skeleton variant="rectangular" width={80} height={22} sx={{ borderRadius: '5px', marginBottom: '16px' }} />
                
                {/* Description lines */}
                <Skeleton variant="text" width="100%" height={18} sx={{ marginBottom: '4px' }} />
                <Skeleton variant="text" width="90%" height={18} sx={{ marginBottom: '4px' }} />
                <Skeleton variant="text" width="70%" height={18} sx={{ marginBottom: '16px' }} />
                
                {/* Progress section */}
                <div style={{ marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={30} height={16} />
                  </div>
                  <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: '4px' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
          📚 My Courses
        </h2>
        <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
          Track your progress and continue learning
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        gap: '12px'
      }}>
        {['all', 'active', 'completed'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              background: filter === f ? '#667eea' : '#ffffff',
              border: filter === f ? '1px solid #667eea' : '1px solid #e2e8f0',
              borderRadius: '6px',
              color: filter === f ? '#ffffff' : '#1a202c',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              textTransform: 'capitalize'
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Courses Grid */}
      {filteredCourses.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '2px dashed #cbd5e0'
        }}>
          <p style={{ fontSize: '48px', margin: '0 0 16px 0' }}>📚</p>
          <h3 style={{ margin: '0 0 8px 0', color: '#1a202c', fontSize: '20px' }}>
            No Courses Found
          </h3>
          <p style={{ color: '#4a5568', marginBottom: '20px', fontSize: '15px' }}>
            {filter === 'all' 
              ? "You're not enrolled in any courses yet"
              : `No ${filter} courses`
            }
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          {filteredCourses.map(course => {
            const enrollment = course.enrollment || {};
            const progress = enrollment.progress || {};
            const percentComplete = progress.percentComplete || 0;

            return (
              <div
                key={course._id}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => navigate(`/course/${course._id}`)}
              >
                {/* Course Thumbnail */}
                {course.thumbnail ? (
                  <div style={{
                    width: '100%',
                    overflow: 'hidden',
                    background: '#f7fafc'
                  }}>
                    <img 
                      src={course.thumbnail} 
                      alt={course.title}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '180px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '48px'
                  }}>
                    📚
                  </div>
                )}
                
                <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1a202c', flex: 1 }}>
                      {course.title}
                    </h3>
                    {getStatusBadge(enrollment.status || 'active')}
                  </div>
                  
                  <span style={{
                    padding: '2px 8px',
                    background: '#f7fafc',
                    color: '#667eea',
                    borderRadius: '5px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {course.code}
                  </span>
                </div>

                {/* Description */}
                {course.description && (
                  <p style={{
                    margin: '0 0 16px 0',
                    color: '#4a5568',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    flex: 1
                  }}>
                    {course.description.length > 100 
                      ? course.description.substring(0, 100) + '...' 
                      : course.description
                    }
                  </p>
                )}

                {/* Progress */}
                <div style={{ marginTop: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#718096' }}>Progress</span>
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: '700', 
                      color: getProgressColor(percentComplete)
                    }}>
                      {percentComplete}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: `${percentComplete}%`,
                      height: '100%',
                      background: `linear-gradient(135deg, ${getProgressColor(percentComplete)} 0%, ${getProgressColor(percentComplete)} 100%)`,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  
                  <div style={{ fontSize: '11px', color: '#718096' }}>
                    {progress.completedAssetsCount || 0} of {progress.totalAssetsCount || 0} lessons completed
                  </div>

                  {enrollment.status === 'completed' && enrollment.completedAt && (
                    <div style={{ fontSize: '11px', color: '#48bb78', marginTop: '8px', fontWeight: '600' }}>
                      ✓ Completed: {new Date(enrollment.completedAt).toLocaleDateString()}
                    </div>
                  )}
                  
                  {enrollment.status === 'active' && enrollment.lastAccessedAt && (
                    <div style={{ fontSize: '11px', color: '#718096', marginTop: '8px' }}>
                      Last accessed: {new Date(enrollment.lastAccessedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Tags */}
                {course.tags && course.tags.length > 0 && (
                  <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {course.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} style={{
                        padding: '3px 8px',
                        background: '#f7fafc',
                        color: '#718096',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '500',
                        border: '1px solid #e2e8f0'
                      }}>
                        {typeof tag === 'object' ? tag.name : tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Continue Button */}
                <button
                  className="btn btn-primary"
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    fontSize: '13px'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/course/${course._id}`);
                  }}
                >
                  {percentComplete === 0 ? 'Start Course' : percentComplete === 100 ? 'Review Course' : 'Continue Learning'} →
                </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {courses.length > 0 && (
        <div style={{
          marginTop: '32px',
          padding: '24px',
          background: '#f7fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c', marginBottom: '16px' }}>
            📊 Learning Summary
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#667eea', marginBottom: '4px' }}>
                {courses.length}
              </div>
              <div style={{ fontSize: '13px', color: '#718096' }}>Total Courses</div>
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#48bb78', marginBottom: '4px' }}>
                {courses.filter(c => c.enrollment?.status === 'completed').length}
              </div>
              <div style={{ fontSize: '13px', color: '#718096' }}>Completed</div>
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ed8936', marginBottom: '4px' }}>
                {courses.filter(c => c.enrollment?.status === 'active').length}
              </div>
              <div style={{ fontSize: '13px', color: '#718096' }}>In Progress</div>
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#667eea', marginBottom: '4px' }}>
                {Math.round(
                  courses.reduce((sum, c) => sum + (c.enrollment?.progress?.percentComplete || 0), 0) / courses.length
                )}%
              </div>
              <div style={{ fontSize: '13px', color: '#718096' }}>Average Progress</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyCourses;
